import { useState, useEffect } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { wideTrips } from "@/lib/tripsFetch";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader, Panel } from "@/components/portal/widgets";
import { buildDays, startOf } from "@/components/portal/VehicleHistoryDialog";
import { dayKey, fmtDate, fmtTime, fmtNow, localToIso } from "@/lib/time";
import { Loader2, Printer, Search } from "lucide-react";

const norm = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
const inp = "rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-blue-600";

// Driver whose trip covers the time, else the closest trip start that day.
function pickAt(rows, atIso) {
  if (!atIso) return null;
  const at = new Date(atIso).getTime();
  let best = null;
  rows.forEach((r) => r.trips.forEach((t) => {
    const s = new Date(startOf(t)).getTime();
    const e = t.dropoff_timestamp ? new Date(t.dropoff_timestamp).getTime() : s;
    const gap = at >= s && at <= e ? 0 : Math.min(Math.abs(at - s), Math.abs(at - e));
    if (!best || gap < best.gap) best = { gap, row: r, trip: t };
  }));
  return best;
}

export default function TicketLookupPage() {
  const { user, organization } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [plate, setPlate] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    Promise.all([fleetApi.get("/vehicles"), fleetApi.get("/drivers")])
      .then(([v, d]) => { setVehicles(v.data || []); setDrivers(d.data || []); })
      .catch((e) => toast.error(fleetError(e, "Couldn't load vehicles.")));
  }, []);

  const lookup = async (e) => {
    e.preventDefault();
    const vehicle = vehicles.find((v) => norm(v.plate) === norm(plate));
    if (!vehicle) { toast.error(`No vehicle with plate “${plate}” in your fleet.`); setResult(null); return; }
    if (!date) { toast.error("Pick the date on the ticket."); return; }
    setBusy(true);
    try {
      const [t, i, inc, l] = await Promise.all([
        (user?.role === "owner" ? fleetApi.get(`/trips/history?start=${date}&end=${date}&limit=500`).then((r) => ({ data: r.data.trips || [] })) : wideTrips(false).then((data) => ({ data }))),
        fleetApi.get("/inspections").catch(() => ({ data: [] })),
        fleetApi.get("/incidents").catch(() => ({ data: [] })),
        api.get(`/vehicle-log/${vehicle.id}`).catch(() => ({ data: [] })),
      ]);
      const trips = (t.data || []).filter((x) => x.assigned_vehicle_id === vehicle.id);
      const ids = new Set(trips.map((x) => x.id));
      const rows = buildDays(trips,
        (Array.isArray(i.data) ? i.data : []).filter((x) => x.vehicle_id === vehicle.id),
        (Array.isArray(inc.data) ? inc.data : []).filter((x) => x.vehicle_id === vehicle.id || (x.trip_id && ids.has(x.trip_id))))
        .filter((r) => r.day === date);
      const atIso = time ? localToIso(`${date} ${time}`) : null;
      const holder = (l.data || []).find((x) => dayKey(x.at) <= date) || null;
      setResult({ vehicle, rows, at: pickAt(rows, atIso), holder, date, time: atIso ? fmtTime(atIso) : time, ref, made: fmtNow() });
    } catch (err) { toast.error(fleetError(err, "Couldn't look up that vehicle.")); }
    finally { setBusy(false); }
  };

  const name = (id, fb) => drivers.find((d) => d.id === id)?.full_name || fb || "Unknown driver";

  return (
    <div data-testid="ticket-lookup-page">
      <div className="print:hidden">
        <PageHeader title="Ticket Lookup" subtitle="Enter the plate and date on a parking or traffic ticket to see who was driving." />
        <Panel testid="ticket-lookup-form-panel">
          <form onSubmit={lookup} className="flex flex-wrap items-end gap-4 p-5" data-testid="ticket-lookup-form">
            <Field label="Plate number"><input value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="MA-632-D56" list="ticket-plates" className={inp} data-testid="ticket-plate-input" required /></Field>
            <datalist id="ticket-plates">{vehicles.map((v) => <option key={v.id} value={v.plate}>{v.fleet_number}</option>)}</datalist>
            <Field label="Date"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inp} data-testid="ticket-date-input" required /></Field>
            <Field label="Time (optional)"><input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inp} data-testid="ticket-time-input" /></Field>
            <Field label="Ticket number (optional)"><input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. BOS-123456" className={inp} data-testid="ticket-ref-input" /></Field>
            <button type="submit" disabled={busy} data-testid="ticket-lookup-submit"
              className="inline-flex items-center gap-2 rounded-full bg-blue-700 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Find driver
            </button>
          </form>
        </Panel>
      </div>
      {result && <TicketNote r={result} name={name} org={organization?.name} by={user?.name || user?.email} />}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</label>{children}</div>
);

function TicketNote({ r, name, org, by }) {
  const v = r.vehicle;
  const used = r.rows.filter((x) => x.trips.length || x.inspections.length);
  const main = (r.at && (r.at.gap <= 2 * 3600e3 || used.length === 1) ? r.at.row : null) || (used.length === 1 ? used[0] : null);
  return (
    <div className="mt-6">
      <div className="mb-3 flex justify-end print:hidden">
        <button onClick={() => window.print()} data-testid="ticket-print-btn"
          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
          <Printer className="h-4 w-4" /> Print note
        </button>
      </div>
      <article id="ticket-note" className="mx-auto max-w-2xl rounded-lg bg-white p-10 text-slate-900 shadow-xl" data-testid="ticket-note">
        <header className="border-b border-slate-300 pb-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Vehicle driver statement</p>
          <h2 className="mt-1 text-2xl font-bold" data-testid="ticket-note-org">{org || "Company"}</h2>
        </header>
        <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Row k="Plate" v={v.plate} t="ticket-note-plate" />
          <Row k="Fleet number" v={v.fleet_number} />
          <Row k="Vehicle" v={`${v.year} ${v.make} ${v.model}`} />
          <Row k="VIN" v={v.vin || "—"} />
          <Row k="Date of ticket" v={fmtDate(`${r.date}T12:00:00`)} t="ticket-note-date" />
          <Row k="Time of ticket" v={r.time || "Not given"} />
          {r.ref && <Row k="Ticket number" v={r.ref} t="ticket-note-ref" />}
        </dl>
        <section className="mt-6 rounded-md border border-slate-300 bg-slate-50 p-4" data-testid="ticket-note-driver">
          {main ? (
            <p className="text-base">The vehicle was driven by <strong>{name(main.driverId, main.driverName)}</strong>{r.at && r.time && main === r.at.row ? (r.at.gap === 0 ? `, who was on a trip at ${r.time}` : `, whose nearest trip that day started at ${fmtTime(startOf(r.at.trip))}`) : ""}.</p>
          ) : used.length > 1 ? (
            <p className="text-base">More than one driver used this vehicle that day: <strong>{used.map((x) => name(x.driverId, x.driverName)).join(", ")}</strong>. Add the ticket time to narrow it down{r.time ? " (no trip was within 2 hours of the time given)" : ""}.</p>
          ) : (
            <p className="text-base">No trips or inspections were recorded on this vehicle on this date.{r.holder?.driver_id ? ` It was assigned to ${name(r.holder.driver_id, r.holder.driver_name)} at the time.` : ""}</p>
          )}
        </section>
        {used.length > 0 && (
          <table className="mt-5 w-full text-left text-sm" data-testid="ticket-note-table">
            <thead><tr className="border-b border-slate-300 text-xs uppercase text-slate-500"><th className="py-1.5">Driver</th><th>Trips</th><th>First – last pickup</th><th>Inspection</th></tr></thead>
            <tbody>
              {used.map((x) => {
                const ts = x.trips.map(startOf).filter(Boolean).sort();
                return (
                  <tr key={`${x.day}|${x.driverId || "?"}`} className="border-b border-slate-200">
                    <td className="py-1.5 font-medium">{name(x.driverId, x.driverName)}</td>
                    <td>{x.trips.length}</td>
                    <td>{ts.length ? `${fmtTime(ts[0])} – ${fmtTime(ts[ts.length - 1])}` : "—"}</td>
                    <td>{x.inspections.length ? "Yes" : "No"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <footer className="mt-8 border-t border-slate-300 pt-3 text-xs text-slate-500">
          Based on trip and inspection records in RideStamp. Prepared by {by} on {r.made}.
          <div className="mt-8 flex gap-10"><span>Signature: ______________________</span><span>Date: ____________</span></div>
        </footer>
      </article>
    </div>
  );
}

const Row = ({ k, v, t }) => (<><dt className="text-slate-500">{k}</dt><dd className="font-medium" data-testid={t}>{v}</dd></>);
